/**
 * Unauthenticated client for the public read-only PAPER
 * (`/api/v1/public/documents/:token`) — the same A4/80mm document staff print,
 * opened by a customer with no login from a WhatsApp link or a printed QR.
 *
 * No bearer, and deliberately its own axios instance rather than api(): an
 * interceptor that attached a stale staff token would turn an anonymous read
 * into an authenticated one.
 */
import axios from 'axios';

/**
 * The server's whole public contract. Shaped to fill DocumentPrintPage's state
 * slots directly, so the public page renders through the shared layout instead
 * of a second copy of it.
 *
 * Note what is NOT here: the customer's email/phone (`parties.phone` doubles as
 * the storefront login id and this token never expires), any GL account, any
 * journal-entry pointer, the sales rep, the channel, item cost, and every
 * internal id bar the ones the paper needs to fetch an image.
 */
/** One "show this paper in X instead" option, as the server resolved it. */
export interface DisplayFxRate {
  code: string;
  /** Multiplier taking ONE unit of the document's currency to `code`. */
  rate: string;
  /** When the rate used is from — printed on the converted paper. */
  asOf: string;
}

export interface PublicPaperDocument {
  docType: 'sales_order' | 'invoice';
  document: {
    docNumber: string;
    docDate: string;
    dueDate: string | null;
    status: string;
    currencyCode: string;
    subtotal: string;
    taxTotal: string;
    discountTotal: string;
    couponCode: string | null;
    total: string;
    amountPaid: string;
    /** The money arrived online through FIB (order, or the invoice its order became). */
    fibPaid?: boolean;
    /** Paid in another currency's notes at the till or the register (see features/documents/cashNotes.ts). */
    cashIn?: { currencyCode: string; amount: string } | null;
    cashBack?: { currencyCode: string; amount: string } | null;
    notes: string | null;
    lines: Array<{
      lineNo: number;
      itemId: string | null;
      itemName: string | null;
      itemImageId: string | null;
      itemBarcode: string | null;
      description: string | null;
      unitName: string | null;
      quantity: string;
      unitPrice: string;
      taxRate: string;
      taxAmount: string;
      lineTotal: string;
    }>;
  };
  party: {
    displayName: string;
    code: string;
    billingAddress: Record<string, unknown> | null;
    taxId: string | null;
  };
  seller: {
    storeName: string | null;
    tagline: string | null;
    logoAttachmentId: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    socialLinks: { website: string | null } | null;
    address: Record<string, unknown> | null;
    slug: string;
  };
  tenantName: string;
  /** The canonical `/d/<token>` URL, echoed back so the printed QR matches the page. */
  publicUrl: string;
  /** Server-rendered QR of `publicUrl` — the page never builds its own. */
  qrSvg: string;
  /** Backend per-currency decimals (code → exponent) so IQD prints as whole dinars. */
  currencyDecimals: Record<string, number>;
  /**
   * Other currencies this paper can be READ in, with today's rate. Display
   * only — the sale keeps the currency it was made in. Optional so a paper
   * served by an API older than this feature still renders.
   */
  fxRates?: DisplayFxRate[];
}

const rawBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const client = axios.create({ baseURL: rawBase, headers: { 'Content-Type': 'application/json' } });

export async function getPublicDocument(token: string): Promise<PublicPaperDocument> {
  return (await client.get<PublicPaperDocument>(`/public/documents/${encodeURIComponent(token)}`)).data;
}
